<template>
  <div>
    <div class="evaluations">
      <!-- POSデフォルト -->
      <div class="content">
        <md-checkbox
          v-for="e in posEvaluations"
          :key="e"
          v-model="_evaluations"
          :value="e"
        >
          {{ $store.state.labels.evaluationsPos[e] }}
        </md-checkbox>
      </div>

      <!-- オプション（データがあれば選択可） -->
      <div class="content" v-if="optionalEvaluations.length > 0">
        <md-checkbox
          v-for="e in optionalEvaluations"
          :key="e"
          v-model="_evaluations"
          :value="e"
        >
          {{ $store.state.labels.evaluationsPos[e] }}
        </md-checkbox>
      </div>
    </div>
  </div>
</template>

<script>
import { intersection } from 'lodash'
import ErrorReport from '@/mixins/ErrorReport'
import EVALUATIONS from '@/const/evaluationsPos'

// 全ての要素がサポートする評価項目に含まれるか否か
function isValidEvaluationValues(values) {
  for (const v of values) {
    if (!EVALUATIONS.includes(v)) {
      return false
    }
  }
  return true
}

/**
 * オプション選択コンポーネント
 *
 * 基本形として定義していて、以下の選択を行うオプションです。
 * * 顧客セグメントの選択（どれか一つ）
 * * 評価項目の選択（任意複数）
 *
 * 指定可能な顧客セグメント、評価項目は絞ることができます（propsを参照してください）。
 *
 * Usage）
 *
 * ```html
 * <filter-option-pos
 *   v-bind.sync="queryFilter.option"
 *   :visible-evaluations="[
 *     'qty',
 *     'amount',
 *     'receipt_count',
 *   ]"
 * ```
 *
 * ```javascript
 * // 略
 * data() {
 *   return {
 *     queryFilter: {
 *       evaluations: ['amount']
 *     }
 *   }
 * }
 * // 略
 * ```
 */
export default {
  mixins: [ErrorReport],
  props: {
    /**
     * 選択肢として表示する評価項目
     *
     * 指定がない場合、指定可能な全ての評価項目を表示します。
     * 空配列を指定された場合、評価項目の選択欄は表示されません。
     */
    visibleEvaluations: {
      type: Array,
      default: () => [...EVALUATIONS],
      validator: isValidEvaluationValues,
    },
    /**
     * 選択されている評価項目
     */
    evaluations: {
      type: Array,
      default: () => [],
      validator: isValidEvaluationValues,
    },
  },
  computed: {
    /**
     * POSの評価項目
     */
    posEvaluations() {
      return intersection(
        [
          'amount',
          'qty',
          'amount_per_qty',
          'qty_pi',
          'amount_pi',
          'receipt_count',
          'sku_count',
          'store_count',
          'avg_ymd',
          'last_sale_ymd',
        ],
        this.visibleEvaluations,
      )
    },
    /**
     * オプションの評価項目
     */
    optionalEvaluations() {
      let evaluations = []
      if (this.$store.state.site.hasLoss) {
        evaluations.push('discount_amount', 'dispose_amount', 'loss_amount')
      }

      if (this.$store.state.site.hasStock) {
        evaluations.push(
          'stock_qty',
          'stock_turn_over_rate',
          'stock_turn_over_ymd',
        )
      }

      return intersection(evaluations, this.visibleEvaluations)
    },
    /**
     * ローカルの評価項目
     *
     * MdCheckboxのv-model用に用意
     */
    _evaluations: {
      get() {
        return this.evaluations
      },
      set(newVal) {
        this.$emit(
          'update:evaluations',
          intersection(this.visibleEvaluations, newVal),
        )
      },
    },
    /**
     * 入力エラー
     */
    errors() {
      const errors = []

      if (!this.evaluations || this.evaluations.length === 0) {
        errors.push('評価項目を選択してください')
      }

      return errors
    },
  },
}
</script>

<style lang="scss" scoped>
.title {
  margin-top: var(--spacing-medium);
}

.evaluations {
  .content:not(:last-child) {
    border-bottom: 1px solid var(--color-border);
  }
}
</style>
